import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import { formatSeconds } from '../../utils/apneaTables';

/**
 * The hold display. Counts UP, deliberately.
 *
 * A countdown turns a breath-hold into a race against a number, and both source
 * texts are explicit that the timer is not in charge. So the primary numeral is
 * elapsed time; the target is context beside it, and passing the target is
 * reported plainly rather than celebrated.
 */

interface HoldCounterProps {
  elapsedSeconds: number;
  /** Prescribed hold, or null for a user-ended hold. */
  targetSeconds: number | null;
  /** Elapsed-at-tap of the first contraction, if marked. */
  firstContractionAt: number | null;
  size?: number;
}

const RING_STROKE = 10;

const HoldCounter: React.FC<HoldCounterProps> = ({
  elapsedSeconds,
  targetSeconds,
  firstContractionAt,
  size = 260,
}) => {
  const theme = useTheme();
  const radius = (size - RING_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;

  const atTarget = targetSeconds !== null && elapsedSeconds >= targetSeconds;
  const overBy = targetSeconds !== null ? Math.max(0, elapsedSeconds - targetSeconds) : 0;

  // Fills toward the target; once past it, the ring stays full rather than
  // wrapping, so "over" reads as a state rather than a new lap.
  const progress = targetSeconds === null || targetSeconds === 0
    ? 0
    : Math.min(1, elapsedSeconds / targetSeconds);

  const ringColor = atTarget ? theme.palette.warning.main : theme.palette.primary.main;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, mx: 'auto' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={alpha(theme.palette.text.primary, 0.12)}
          strokeWidth={RING_STROKE}
        />
        {targetSeconds !== null && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={ringColor}
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - progress)}
            style={{ transition: 'stroke-dashoffset 0.2s linear, stroke 0.3s' }}
          />
        )}
      </svg>

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color: atTarget ? 'warning.main' : 'text.primary',
            lineHeight: 1,
          }}
          aria-live="off"
        >
          {formatSeconds(elapsedSeconds)}
        </Typography>

        {targetSeconds !== null && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {overBy > 0
              ? `+${formatSeconds(overBy)} over ${formatSeconds(targetSeconds)}`
              : `target ${formatSeconds(targetSeconds)}`}
          </Typography>
        )}

        {targetSeconds === null && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            hold as long as is comfortable
          </Typography>
        )}

        {firstContractionAt !== null && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            first contraction {formatSeconds(firstContractionAt)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default HoldCounter;
